import * as React from "react"

import { Button, Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import { OverviewSurface } from "@/features/finance/model/finance-normalized-overview"
import { readFinanceCashPosition } from "@/features/finance/lib/finance-overview-integration"
import type { FinanceOverviewQuery } from "@/features/finance/model/finance-types"
import type { DisplayAmount } from "@/shared/lib/formatting/currency"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"
import { formatMoneySafe, formatShare } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import { type FinanceSurfaceContext } from "@/features/finance/hooks/finance-context-rules"
import { WidgetShareNote } from "@/features/finance/overview/widgets/widget-share-note"
import { useIsWithheld } from "@/features/finance/overview/widgets/widget-withheld-state"
import {
  useWidgetCreateActions,
  type WidgetCreateKey,
} from "@/features/finance/overview/lib/use-widget-create-actions"

import {
  ConfigSwitchRow,
  WidgetConfigDrawer,
  useWidgetConfig,
} from "@/features/finance/overview/components/widget-config-drawer"

const CREATE_KEYS: readonly WidgetCreateKey[] = ["income", "expense", "transfer"]

interface TotalCashConfig {
  hideZero: boolean
  showCount: boolean
}

interface TotalCashData {
  /** Money: the backend's total over EVERY account in scope, as a canonical decimal string (IBA-116/117). */
  total: DisplayAmount
  currency: string
  accountCount: number
  nonZeroAccountCount: number
  hasAccounts: boolean
}

/**
 * Total Cash — the current total held across real monetary accounts. Every FinanceAccount is an ASSET/cash pot
 * (the schema has no non-cash account sub-type), and the BACKEND sums their current ledger balances over EVERY
 * account in scope (IBA-117). This widget used to fetch one page of accounts and add them up in the browser, so
 * a church with more than 100 accounts silently displayed a total that was too small — quietly, and worse the
 * bigger the church. No widget re-computes a total the backend already sent.
 *
 * The figure itself is as-of-now: it is the money held today, and the dashboard period does not change it. It
 * rides the shared finance-overview query (same key as the other widgets, so no extra request).
 */
export function TotalCashWidget({ context }: { context?: FinanceSurfaceContext } = {}) {
  const isWithheld = useIsWithheld(context)
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const period = useFinanceOverviewPeriod()
  const go = useOverviewNav()
  const { useFinanceOverviewQuery } = useFinanceFeatureQuery()

  const query = useFinanceOverviewQuery(
    scope.workspaceId && period.from && period.to
      ? ({
          workspaceId: scope.workspaceId,
          context: scope.overviewContext,
          branchId: scope.branchId ?? undefined,
          dateFrom: period.from,
          dateTo: period.to,
          profile: OverviewSurface.Dashboard,
        } satisfies FinanceOverviewQuery)
      : null,
  )

  const cfg = useWidgetConfig<TotalCashConfig>({ hideZero: false, showCount: true })

  const data = React.useMemo<TotalCashData | undefined>(() => {
    const cash = readFinanceCashPosition(query.data)
    if (!cash) return undefined
    // `hideZero` cannot move the total — an account holding zero adds zero to a sum. It only selects which
    // count is honest to show underneath.
    return {
      total: cash.totalCash,
      currency: query.data?.meta?.currency ?? scope.currency,
      accountCount: cash.accountCount,
      nonZeroAccountCount: cash.nonZeroAccountCount,
      hasAccounts: cash.accountCount > 0,
    }
  }, [query.data, scope.currency])

  const create = useWidgetCreateActions(CREATE_KEYS)
  useWidgetMenu({
    configure: cfg.openDrawer,
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.accounts) ? () => go(OverviewRoutes.accounts) : undefined,
    create,
  })

  return (
    <>
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={data}
      isEmpty={(d) => !d.hasAccounts}
      skeleton={<TotalCashSkeleton />}
      errorTitle={t("widgets.totalCash.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.totalCash.empty.title")}
      emptyAction={
        // Hidden on the consolidated board: this figure spans every branch, so "view accounts" could only
        // open the ACTIVE branch's — a different set from the one the number describes.
        scope.canEdit && go.canNavigateTo(OverviewRoutes.accounts) ? (
          <Button size="sm" variant="outline" onClick={() => go(OverviewRoutes.accounts)}>
            {t("widgets.totalCash.empty.action")}
          </Button>
        ) : undefined
      }
    >
      {(d) => (
        <div className="space-y-2">
          <p className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">
            {/*
              * Formatted from the board's CONTEXT, not from the payload's currency.
              *
              * The sentinel swap depends on `meta.figuresAreRatios` surviving the wire; when it did not, the
              * card printed "RWF 100" for a value that was already the percentage 100 — a currency glued to a
              * share. Two sources deciding the same thing is how they disagree. The context is the one that
              * also drives the caption beneath, so they cannot contradict each other.
              */}
            {isWithheld ? formatShare(d.total) : formatMoneySafe(d.total, d.currency)}
          </p>
          {/* Says WHY the figure is a share, from the board's context — never by inspecting the payload. */}
          <WidgetShareNote context={context} />
          {cfg.config.showCount ? (
            <p className="text-xs text-muted-foreground">
              {t("widgets.totalCash.accountsIncluded", {
                count: cfg.config.hideZero ? d.nonZeroAccountCount : d.accountCount,
              })}
            </p>
          ) : null}
          {go.canNavigateTo(OverviewRoutes.accounts) ? (
          <button
            type="button"
            onClick={() => go(OverviewRoutes.accounts)}
            className="text-xs font-medium text-primary hover:underline"
          >
            {t("widgets.totalCash.viewAccounts")}
          </button>
          ) : null}
        </div>
      )}
    </WidgetBoundary>

    <WidgetConfigDrawer
      open={cfg.open}
      title={t("widgets.totalCash.title")}
      onSave={cfg.save}
      onCancel={cfg.cancel}
    >
      <ConfigSwitchRow
        label={t("widgets.totalCash.config.hideZero")}
        checked={cfg.draft.hideZero}
        onCheckedChange={(value) => cfg.patch({ hideZero: value })}
      />
      <ConfigSwitchRow
        label={t("widgets.totalCash.config.showCount")}
        checked={cfg.draft.showCount}
        onCheckedChange={(value) => cfg.patch({ showCount: value })}
      />
    </WidgetConfigDrawer>
    </>
  )
}

function TotalCashSkeleton() {
  return (
    <div className="space-y-2.5">
      <Skeleton className="h-7 w-32" />
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-3 w-20" />
    </div>
  )
}
